import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PasswordInput } from "../components/ui/password-input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { BackBar } from "../components/BackBar";

export default function LoginPage() {
  const { t } = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirectIntent = params.get("redirect");  // e.g. "studentlife"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (["admissions_admin", "academic_admin", "finance_admin", "teacher", "super_admin"].includes(u.role)) {
        nav("/admin");
        return;
      }
      // Parent: if they clicked "Vie scolaire" pillar, try to send them straight to their student's SIS
      if (redirectIntent === "studentlife") {
        try {
          const { data: enrollments } = await api.get("/enrollments/mine");
          const enrolled = enrollments.find(e => e.student_id);
          if (enrolled) {
            nav(`/parent/student/${enrolled.student_id}?tab=schedule`);
            return;
          }
        } catch { /* fall through */ }
        // No enrolled student yet → land on dashboard with a friendly toast
        toast.info(t("auth.noEnrollmentYet"));
      }
      nav("/parent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-12" data-testid="login-page">
      <BackBar to="/" fallbackLabelFr="Accueil" fallbackLabelEn="Home" />
      <p className="overline mb-3">OFA France</p>
      <h1 className="font-serif text-4xl tracking-tighter mb-2">{t("auth.loginTitle")}</h1>
      <p className="text-muted-foreground mb-8 text-sm">
        {t("auth.loginIntro")}
      </p>
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <Label className="text-sm">{t("common.email")}</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm">{t("common.password")}</Label>
          <PasswordInput data-testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          <div className="mt-1.5 text-right">
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-accent" data-testid="goto-forgot">
              {t("auth.forgotPassword")}
            </Link>
          </div>
        </div>
        <Button data-testid="login-submit" type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground">
          {submitting ? t("common.loading") : t("auth.loginCta")}
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground">
        {t("auth.noAccount")} <Link to="/register" className="text-accent font-medium" data-testid="goto-register">{t("auth.registerHere")}</Link>
      </div>
    </div>
  );
}
